import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Snackbar, Typography } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import { getWebUsers } from "../../api/webUserAPI";
import { useSnackbar } from "../../hooks/useSnackbar";
import InviteUserDialog from "../users/InviteUserDialog";
import { GhostButton, ListGrid, Panel, PrimaryButton } from "./ui";
import { DP } from "./constants";
import { isSuperAdminUser } from "./roleUtils";

const FIELD_ORDER = [
  "username",
  "firstName",
  "lastName",
  "email",
  "roles",
  "createdTimestamp",
];

const HEADER_NAMES = {
  id: "Kullanıcı ID",
  idwebUser: "Kullanıcı ID",
  username: "Kullanıcı Adı",
  firstName: "Ad",
  lastName: "Soyad",
  email: "E-posta",
  group1: "Grup 1",
  group2: "Grup 2",
  group3: "Grup 3",
  roles: "Roller",
  enabled: "Durum",
  createdTimestamp: "Tarih",
  createTime: "Tarih",
  updateTime: "Güncellenme Tarihi",
};

const humanizeField = (field) =>
  String(field)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toLocaleUpperCase("tr-TR"));

const formatValue = (value, field) => {
  if (value === null || value === undefined) return "";
  const normalizedField = field.toLocaleLowerCase("en-US");
  if (
    normalizedField.includes("timestamp") ||
    normalizedField.includes("createtime") ||
    normalizedField.includes("updatetime")
  ) {
    const timestamp =
      typeof value === "number" && value < 1000000000000 ? value * 1000 : value;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("tr-TR");
  }
  if (normalizedField === "enabled") return value ? "Aktif" : "Pasif";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

export default function UserDefinitionsPanel() {
  // Süper admin'in kullanıcı yönetimi ekranı henüz hazır değil; admin mevcut
  // listeyi görmeye devam eder. Ayrı bileşen: liste isteği hiç atılmıyor.
  return isSuperAdminUser() ? <UnderDevelopment /> : <UserList />;
}

function UnderDevelopment() {
  return (
    <Panel title="Kullanıcı Tanımları" sub="Geliştirme aşamasında">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 8,
          textAlign: "center",
          color: DP.textMuted,
        }}
      >
        <ConstructionRoundedIcon sx={{ fontSize: 52, color: DP.warning }} />
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: DP.textMain }}>
          Bu ekran hazırlanıyor
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", maxWidth: 420 }}>
          Süper admin kullanıcı yönetimi geliştirme aşamasında. Yayına alındığında
          tüm firmaların kullanıcılarını buradan yönetebileceksiniz.
        </Typography>
      </Box>
    </Panel>
  );
}

function UserList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getWebUsers();
      const users = Array.isArray(result) ? result : [];
      setRows(
        users.map((user, index) => ({
          ...(user && typeof user === "object" ? user : { value: user }),
          __userRowId:
            user?.id ??
            user?.idwebUser ??
            user?.idWebUser ??
            user?.username ??
            index,
        }))
      );
    } catch (error) {
      setRows([]);
      showSnackbar(error.message || "Kullanıcı listesi alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInviteSuccess = () => {
    showSnackbar("Kullanıcı daveti gönderildi", "success");
    load();
  };

  // Sabit kolon listesi: API çok fazla alan döndürüyor, sadece bunları göster.
  const columns = useMemo(
    () =>
      FIELD_ORDER.map((field) => ({
        field,
        headerName: HEADER_NAMES[field] || humanizeField(field),
        flex: 1,
        minWidth: field === "email" ? 200 : 130,
        valueFormatter: (value) => formatValue(value, field),
      })),
    []
  );

  return (
    <>
      <Panel
        title="Kullanıcı Tanımları"
        sub={`${rows.length} kullanıcı`}
        action={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <PrimaryButton
              startIcon={<PersonAddAltRoundedIcon />}
              onClick={() => setInviteDialogOpen(true)}
            >
              Kullanıcı Davet Et
            </PrimaryButton>
            <GhostButton
              startIcon={<RefreshRoundedIcon />}
              onClick={load}
              disabled={loading}
            >
              Yenile
            </GhostButton>
          </Box>
        }
      >
        <Box sx={{ mt: 0.5 }}>
          <ListGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.__userRowId}
            loading={loading}
            height={560}
          />
        </Box>
      </Panel>

      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
